/**
 * SemanticFabric — "Connect any model. Keep one governed contract."
 *
 * Same content, routes and `fabric-fx` namespace as before. What changed:
 *  - The diagram now answers the selector. Models plug into the chip through one
 *    bus, and the boundary around the system changes with the deployment
 *    (gateway → your VPC → your boundary) while the chip, the governed
 *    contract, stays identical. That contrast is the argument of the section.
 *  - Options auto-advance with a hairline progress indicator until the person
 *    interacts. It pauses on hover/focus and when off screen, and never runs
 *    with reduced motion.
 *  - The chip assembles layer by layer the first time it is seen.
 *  - Accordion a11y: aria-controls, labelled regions, `inert` when collapsed,
 *    visible focus, live status line.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { Check, ChevronDown, Cloud, Cpu, Server } from "lucide-react";

const OPTIONS = [
  {
    id: "managed",
    label: "Managed models",
    text: "Route to OpenAI, Anthropic, Google or your preferred provider through one policy-aware gateway.",
    Icon: Cloud,
    models: ["OpenAI", "Anthropic", "Google"],
    boundary: "Policy-aware gateway",
    note: "routed through one gateway",
  },
  {
    id: "private",
    label: "Private models",
    text: "Run approved models in your VPC with the same grounded context, permissions and controls.",
    Icon: Server,
    models: ["Model A", "Model B", "Model C"],
    boundary: "Your VPC",
    note: "running inside your VPC",
  },
  {
    id: "local",
    label: "On-prem & edge",
    text: "Keep sensitive workloads inside your boundary without rebuilding retrieval, governance or audit logic.",
    Icon: Cpu,
    models: ["On-prem model", "Edge model", "Local runtime"],
    boundary: "Your boundary",
    note: "kept inside your boundary",
  },
] as const;
type OptionId = (typeof OPTIONS)[number]["id"];

const LAYER = (index: number) => ({ "--i": index }) as CSSProperties;

export default function SemanticFabric() {
  const [active, setActive] = useState<OptionId>("managed");
  const [auto, setAuto] = useState(true);
  const [motion, setMotion] = useState<"idle" | "armed" | "seen">("idle");
  const [inView, setInView] = useState(false);
  const [reduced, setReduced] = useState(true);
  const shellRef = useRef<HTMLDivElement>(null);

  const index = OPTIONS.findIndex((option) => option.id === active);
  const selected = OPTIONS[index];

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setMotion("seen");
      return;
    }
    setReduced(false);
    setMotion("armed");
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) setMotion("seen");
      },
      { threshold: 0.35 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const choose = (id: OptionId) => {
    setAuto(false);
    setActive(id);
  };
  const advance = () => setActive(OPTIONS[(index + 1) % OPTIONS.length].id);

  return (
    <section id="architecture" className="fabric-fx">
      <div className="container">
        <div className="fabric-shell" ref={shellRef} data-motion={motion} data-inview={inView}>
          <div className="fabric-copy">
            <div className="section-eyebrow">How it works</div>
            <h2>
              Connect any model.<span>Keep one governed contract.</span>
            </h2>
            <p className="fabric-intro">
              Compose enterprise context once. Ontoligent applies the same definitions, permissions and evidence
              rules to every model and every deployment.
            </p>

            <div className="fabric-options" role="list" aria-label="Model deployment options">
              {OPTIONS.map(({ id, label, text, Icon }) => {
                const open = active === id;
                return (
                  <div className="fabric-option" data-active={open || undefined} key={id} role="listitem">
                    <button
                      type="button"
                      id={`fabric-btn-${id}`}
                      aria-expanded={open}
                      aria-controls={`fabric-detail-${id}`}
                      onClick={() => choose(id)}
                    >
                      <Icon size={21} aria-hidden />
                      <span>{label}</span>
                      <ChevronDown size={18} aria-hidden />
                    </button>
                    <div
                      className="fabric-detail"
                      id={`fabric-detail-${id}`}
                      role="region"
                      aria-labelledby={`fabric-btn-${id}`}
                      inert={!open}
                    >
                      <p>{text}</p>
                    </div>
                    {open && auto && !reduced && <i className="fabric-progress" aria-hidden onAnimationEnd={advance} />}
                  </div>
                );
              })}
            </div>

            <div className="fabric-cta">
              <Link to="/studio/composer" className="btn btn-primary">
                Explore Architecture Studio
              </Link>
              <Link to="/docs" className="btn btn-ghost">
                Read the architecture guide
              </Link>
            </div>
          </div>

          <div className="fabric-visual" role="group" aria-label={`${selected.label} architecture diagram`} data-mode={active}>
            <div className="fabric-grid-bg" aria-hidden />
            <div className="fabric-stage">
              <div className="fabric-boundary" aria-hidden>
                <span key={active}>{selected.boundary}</span>
              </div>
              <div className="fabric-models" key={active} aria-hidden>
                {selected.models.map((model, i) => (
                  <span className="fabric-pill" style={LAYER(i)} key={model}>
                    <i />
                    {model}
                  </span>
                ))}
              </div>
              <div className="fabric-bus" aria-hidden />

              <div className="fabric-chip-wrap">
                <svg className="fabric-chip" viewBox="90 50 476 390" aria-hidden preserveAspectRatio="xMidYMid meet">
                  <g className="chip-guides" fill="none" strokeWidth="0.8" strokeDasharray="2 6">
                    <path d="M90 234 328 96l238 138-238 138Z" />
                    <path d="M137 234 328 123l191 111-191 111Z" />
                    <path d="m194 77 328 190M134 112l328 190M74 147l328 190M462 77 134 267M522 112 194 302M582 147 254 337" />
                  </g>
                  <g className="chip-enter" style={LAYER(0)}>
                    <g className="chip-layer chip-base">
                      <path d="M144 312 328 206l184 106-184 107Z" />
                      <path className="chip-side" d="M144 312v13l184 107 184-107v-13L328 419Z" />
                      <path className="chip-inset" d="M178 310 328 223l150 87-150 87Z" />
                      <g className="chip-modules">
                        <path d="m174 317 42-24 37 21-42 25Z" />
                        <path d="m403 314 37-21 42 24-37 22Z" />
                        <path d="m257 375 71-41 71 41-71 41Z" />
                      </g>
                      <g className="chip-traces">
                        <path d="m166 314 44-25 47 27 33-19" />
                        <path d="m490 314-44-25-47 27-33-19" />
                        <path d="m236 362 48-28 44 25 44-25 48 28" />
                      </g>
                      <g className="chip-nodes">
                        <circle cx="210" cy="289" r="4" />
                        <circle cx="446" cy="289" r="4" />
                        <circle cx="328" cy="359" r="4" />
                      </g>
                      <text x="328" y="402">ENTERPRISE CONTEXT</text>
                    </g>
                  </g>
                  <g className="chip-enter" style={LAYER(1)}>
                    <g className="chip-layer chip-middle">
                      <path d="M144 267 328 161l184 106-184 107Z" />
                      <path className="chip-side" d="M144 267v16l184 107 184-107v-16L328 374Z" />
                      <path className="chip-inset" d="M177 267 328 180l151 87-151 88Z" />
                      <path className="chip-trace-static" d="m144 267 49 28 16-9 50 29 18-10M512 267l-49 28-16-9-50 29-18-10" />
                      <circle cx="209" cy="286" r="3" />
                      <circle cx="447" cy="286" r="3" />
                      <text x="328" y="356">INTELLIGENCE RUNTIME</text>
                    </g>
                  </g>
                  <g className="chip-enter" style={LAYER(2)}>
                    <g className="chip-layer chip-upper">
                      <path d="M144 223 328 117l184 106-184 107Z" />
                      <path className="chip-side" d="M144 223v14l184 107 184-107v-14L328 330Z" />
                      <path className="chip-inset" d="m177 224 151-88 151 88-151 87Z" />
                      <text x="328" y="311">RETRIEVAL + ACTIONS</text>
                    </g>
                  </g>
                  <g className="chip-enter" style={LAYER(3)}>
                    <g className="chip-layer chip-top">
                      <path d="M134 177 328 65l194 112-194 112Z" />
                      <path className="chip-side" d="M134 177v13l194 112 194-112v-13L328 289Z" />
                      <path className="chip-inset" d="m165 177 163-94 163 94-163 94Z" />
                      <g className="chip-processor">
                        <path d="m278 177 50-29 50 29-50 29Z" />
                        <path d="m288 177 40-23 40 23-40 23Z" />
                        <path d="m303 177 25-14 25 14-25 15Z" />
                        <path className="chip-pins" d="M278 177h-24m124 0h24M328 148v-22m0 80v22" />
                        <circle cx="254" cy="177" r="3" />
                        <circle cx="402" cy="177" r="3" />
                        <circle cx="328" cy="126" r="3" />
                        <circle cx="328" cy="228" r="3" />
                        <circle className="chip-pulse" cx="328" cy="177" r="5" />
                      </g>
                      <text x="328" y="253">GOVERNED CONTRACT</text>
                    </g>
                  </g>
                </svg>
              </div>

              <ul className="fabric-contract" aria-label="Constant across every deployment">
                {["Same definitions", "Same permissions", "Same evidence rules"].map((item) => (
                  <li key={item}>
                    <Check size={11} aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="fabric-status" aria-live="polite">
              <span>
                <i /> {selected.label}
              </span>
              <span>policy checks passed &middot; {selected.note}</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .fabric-fx{--fabric-mono:'IBM Plex Mono',ui-monospace,monospace;--fabric-ease:cubic-bezier(.2,.7,.1,1);--fabric-autoplay:7s}
        .fabric-fx .container{max-width:1280px;margin:0 auto;padding:0 32px}
        .fabric-fx .fabric-shell{display:grid;grid-template-columns:minmax(320px,.78fr) minmax(500px,1.22fr);min-height:700px;overflow:hidden;border:1px solid hsl(var(--border));background:hsl(var(--panel)/.32)}

        /* copy + selector */
        .fabric-fx .fabric-copy{padding:clamp(36px,5vw,72px);border-right:1px solid hsl(var(--border))}
        .fabric-fx .fabric-copy h2{margin:13px 0 22px;font-size:clamp(2.7rem,4vw,4.9rem);line-height:.96;letter-spacing:-.045em;text-wrap:balance}
        .fabric-fx .fabric-copy h2 span{display:block;color:hsl(var(--accent))}
        .fabric-fx .fabric-intro{max-width:48ch;margin:0 0 34px;color:hsl(var(--muted));font-size:16px;line-height:1.65}
        .fabric-fx .fabric-options{border-top:1px solid hsl(var(--border))}
        .fabric-fx .fabric-option{position:relative;border-bottom:1px solid hsl(var(--border))}
        .fabric-fx .fabric-option button{display:grid;width:100%;grid-template-columns:28px 1fr auto;align-items:center;gap:10px;padding:19px 2px;border:0;background:none;color:hsl(var(--muted));font:inherit;text-align:left;cursor:pointer;transition:color .2s}
        .fabric-fx .fabric-option button:hover,.fabric-fx .fabric-option[data-active] button{color:hsl(var(--text))}
        .fabric-fx .fabric-option button:focus-visible{outline:2px solid hsl(var(--accent));outline-offset:2px}
        .fabric-fx .fabric-option button span{font-size:15px;font-weight:650}
        .fabric-fx .fabric-option button svg:first-child{transition:color .2s}
        .fabric-fx .fabric-option[data-active] button svg:first-child{color:hsl(var(--accent))}
        .fabric-fx .fabric-option button svg:last-child{transition:transform .25s}
        .fabric-fx .fabric-option[data-active] button svg:last-child{transform:rotate(180deg);color:hsl(var(--accent))}
        .fabric-fx .fabric-detail{display:grid;grid-template-rows:0fr;transition:grid-template-rows .3s var(--fabric-ease)}
        .fabric-fx .fabric-detail p{min-height:0;margin:0;overflow:hidden;color:hsl(var(--muted));font-size:13px;line-height:1.55;opacity:0;transition:opacity .25s ease}
        .fabric-fx .fabric-option[data-active] .fabric-detail{grid-template-rows:1fr}
        .fabric-fx .fabric-option[data-active] .fabric-detail p{padding:0 34px 19px 38px;opacity:1;transition-delay:.08s}
        .fabric-fx .fabric-progress{position:absolute;right:0;bottom:-1px;left:0;height:1px;transform-origin:left;background:hsl(var(--accent));transform:scaleX(0)}
        .fabric-fx .fabric-cta{display:flex;flex-wrap:wrap;gap:10px;margin-top:34px}

        /* visual */
        .fabric-fx .fabric-visual{position:relative;display:flex;flex-direction:column;justify-content:center;min-height:700px;padding:76px 5% 58px;overflow:hidden;isolation:isolate;background:hsl(var(--bg))}
        .fabric-fx .fabric-visual::before{content:"MODEL-AGNOSTIC / POLICY-BOUND";position:absolute;z-index:3;top:24px;left:26px;color:hsl(var(--muted));font:9px/1 var(--fabric-mono);letter-spacing:.14em}
        .fabric-fx .fabric-grid-bg{position:absolute;inset:0;opacity:.55;background-image:linear-gradient(hsl(var(--border)/.45) 1px,transparent 1px),linear-gradient(90deg,hsl(var(--border)/.45) 1px,transparent 1px);background-size:32px 32px;mask-image:radial-gradient(circle at 50% 48%,black,transparent 88%)}

        /* deployment boundary: the one thing that changes around the chip */
        .fabric-fx .fabric-boundary{position:absolute;z-index:-1;top:70px;right:-14px;bottom:-18px;left:-14px;border:1px solid hsl(var(--accent)/.38);background:hsl(var(--accent)/.025);transition:top .6s var(--fabric-ease),border-color .4s ease,background .4s ease,box-shadow .4s ease}
        .fabric-fx .fabric-boundary span{position:absolute;top:0;left:16px;transform:translateY(-50%);padding:3px 8px;background:hsl(var(--bg));color:hsl(var(--accent));font:9px/1 var(--fabric-mono);letter-spacing:.12em;text-transform:uppercase}
        .fabric-fx [data-mode="private"] .fabric-boundary{top:-18px;border-style:dashed;border-color:hsl(var(--accent)/.6);background:hsl(var(--accent)/.035)}
        .fabric-fx [data-mode="local"] .fabric-boundary{top:-18px;border-color:hsl(var(--accent)/.9);background:hsl(var(--accent)/.05);box-shadow:inset 0 0 0 4px hsl(var(--bg)),inset 0 0 0 5px hsl(var(--accent)/.3)}

        .fabric-fx .fabric-stage{position:relative;z-index:2;display:flex;flex-direction:column;width:100%}
        .fabric-fx .fabric-models{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:0 4%}
        .fabric-fx .fabric-pill{display:flex;align-items:center;justify-content:center;gap:8px;height:38px;padding:0 10px;border:1px solid hsl(var(--border));background:hsl(var(--panel));color:hsl(var(--text));font:10px/1 var(--fabric-mono);letter-spacing:.08em;text-transform:uppercase;white-space:nowrap}
        .fabric-fx .fabric-pill i{width:6px;height:6px;border-radius:50%;background:hsl(var(--accent));box-shadow:0 0 0 3px hsl(var(--accent)/.14)}
        .fabric-fx .fabric-bus{position:relative;flex:0 0 40px;margin:0 4%}
        .fabric-fx .fabric-bus::before{content:"";position:absolute;top:0;right:calc(100%/6);left:calc(100%/6);height:20px;border:1px solid hsl(var(--accent)/.55);border-top:0}
        .fabric-fx .fabric-bus::after{content:"";position:absolute;top:20px;bottom:0;left:50%;width:1px;background:repeating-linear-gradient(180deg,hsl(var(--accent)) 0 4px,transparent 4px 8px);background-size:1px 8px}
        .fabric-fx .fabric-chip-wrap{display:block}
        .fabric-fx .fabric-chip{display:block;width:100%;height:auto;max-height:500px;overflow:hidden;-webkit-mask-image:linear-gradient(90deg,transparent,#000 9%,#000 91%,transparent);mask-image:linear-gradient(90deg,transparent,#000 9%,#000 91%,transparent)}
        .fabric-fx .fabric-contract{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:18px 4% 0;padding:0;list-style:none}
        .fabric-fx .fabric-contract li{display:flex;align-items:center;justify-content:center;gap:7px;padding-top:11px;border-top:1px solid hsl(var(--accent)/.4);color:hsl(var(--muted));font:9px/1.2 var(--fabric-mono);letter-spacing:.1em;text-transform:uppercase;white-space:nowrap}
        .fabric-fx .fabric-contract svg{color:hsl(var(--accent))}
        .fabric-fx .chip-guides{stroke:hsl(var(--border));opacity:.62}
        .fabric-fx .chip-layer>path:first-child{fill:hsl(var(--panel)/.88);stroke:hsl(var(--accent));stroke-width:1.45}
        .fabric-fx .chip-layer .chip-side{fill:hsl(var(--bg)/.9);stroke:hsl(var(--accent)/.8);stroke-width:1.35}
        .fabric-fx .chip-layer .chip-inset{fill:none;stroke:hsl(var(--accent)/.42);stroke-width:1;stroke-dasharray:3 5}
        .fabric-fx .chip-middle>path:first-child{fill:hsl(var(--panel)/.82);stroke:hsl(var(--accent)/.72)}
        .fabric-fx .chip-upper>path:first-child{fill:hsl(var(--panel)/.9);stroke:hsl(var(--accent)/.62)}
        .fabric-fx .chip-top>path:first-child{fill:hsl(var(--panel));stroke:hsl(var(--accent));stroke-width:1.8}
        .fabric-fx .chip-top{transform-box:fill-box;transform-origin:center}
        .fabric-fx .chip-modules path{fill:hsl(var(--accent)/.08);stroke:hsl(var(--accent));stroke-width:1}
        .fabric-fx .chip-traces,.fabric-fx .chip-trace-static,.fabric-fx .chip-pins{fill:none;stroke:hsl(var(--accent));stroke-width:1.2;stroke-linecap:round;stroke-linejoin:round}
        .fabric-fx .chip-traces path{stroke-dasharray:7 8}
        .fabric-fx .chip-nodes circle,.fabric-fx .chip-middle circle,.fabric-fx .chip-processor circle{fill:hsl(var(--panel));stroke:hsl(var(--accent));stroke-width:1}
        .fabric-fx .chip-processor path:not(.chip-pins){fill:hsl(var(--bg));stroke:hsl(var(--accent));stroke-width:1.3}
        .fabric-fx .chip-processor path:nth-child(2){fill:hsl(var(--accent)/.12)}
        .fabric-fx .chip-processor path:nth-child(3){fill:hsl(var(--panel))}
        .fabric-fx .chip-processor .chip-pulse{transform-box:fill-box;transform-origin:center;fill:hsl(var(--accent))}
        .fabric-fx .fabric-chip text{fill:hsl(var(--text)/.72);font:600 8px/1 var(--fabric-mono);letter-spacing:.13em;text-anchor:middle;paint-order:stroke;stroke:hsl(var(--panel));stroke-width:3px}
        .fabric-fx .chip-top text{fill:hsl(var(--accent))}
        .fabric-fx .fabric-status{position:absolute;z-index:3;right:18px;bottom:17px;left:18px;display:flex;justify-content:space-between;gap:16px;color:hsl(var(--muted));font:9px/1 var(--fabric-mono);letter-spacing:.08em;text-transform:uppercase}
        .fabric-fx .fabric-status i{display:inline-block;width:6px;height:6px;margin-right:6px;border-radius:50%;background:hsl(var(--accent));box-shadow:0 0 0 4px hsl(var(--accent)/.13)}

        /* motion */
        .fabric-fx [data-motion="armed"] .chip-enter,.fabric-fx [data-motion="armed"] .fabric-stage .fabric-models,.fabric-fx [data-motion="armed"] .fabric-bus,.fabric-fx [data-motion="armed"] .fabric-contract{opacity:0}
        @media (prefers-reduced-motion:no-preference){
          .fabric-fx [data-motion="seen"] .chip-enter{animation:chip-rise .9s var(--fabric-ease) calc(var(--i)*.16s) both}
          .fabric-fx [data-motion="seen"] .fabric-bus,.fabric-fx [data-motion="seen"] .fabric-contract{animation:fabric-fade .6s ease .8s both}
          .fabric-fx .fabric-pill{animation:fabric-pill-in .5s var(--fabric-ease) calc(var(--i)*.07s) both}
          .fabric-fx .fabric-boundary span{animation:fabric-fade .4s ease .15s both}
          .fabric-fx .fabric-bus::after{animation:fabric-flow .9s linear infinite}
          .fabric-fx .chip-top{animation:chip-float 7s ease-in-out infinite}
          .fabric-fx .chip-traces path{animation:chip-trace 12s linear infinite}
          .fabric-fx .chip-traces path:nth-child(2){animation-delay:-4s}
          .fabric-fx .chip-traces path:nth-child(3){animation-delay:-8s}
          .fabric-fx .chip-processor .chip-pulse{animation:chip-pulse 4.8s ease-in-out infinite}
          .fabric-fx .fabric-progress{animation:fabric-progress var(--fabric-autoplay) linear forwards}
          .fabric-fx .fabric-shell:hover .fabric-progress,.fabric-fx .fabric-shell:focus-within .fabric-progress,.fabric-fx .fabric-shell[data-inview="false"] .fabric-progress{animation-play-state:paused}
          @keyframes chip-rise{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:none}}
          @keyframes fabric-fade{from{opacity:0}to{opacity:1}}
          @keyframes fabric-pill-in{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:none}}
          @keyframes fabric-flow{to{background-position:0 8px}}
          @keyframes fabric-progress{to{transform:scaleX(1)}}
          @keyframes chip-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
          @keyframes chip-trace{to{stroke-dashoffset:-60}}
          @keyframes chip-pulse{0%,100%{opacity:.68;transform:scale(.92)}50%{opacity:1;transform:scale(1.12)}}
        }

        @media (max-width:960px){
          .fabric-fx .fabric-shell{grid-template-columns:1fr}
          .fabric-fx .fabric-copy{border-right:0;border-bottom:1px solid hsl(var(--border))}
          .fabric-fx .fabric-visual{min-height:640px}
        }
        @media (max-width:620px){
          .fabric-fx .container{padding:0 18px}
          .fabric-fx .fabric-copy{padding:32px 22px}
          .fabric-fx .fabric-visual{min-height:560px}
          .fabric-fx .fabric-visual{padding:64px 6% 52px}
          .fabric-fx .fabric-contract{gap:6px;margin:14px 0 0}
          .fabric-fx .fabric-contract li{flex-direction:column;gap:4px;font-size:8px;letter-spacing:.05em;white-space:normal;text-align:center}
          .fabric-fx .fabric-models{gap:6px;margin:0}
          .fabric-fx .fabric-bus{margin:0}
          .fabric-fx .fabric-pill{padding:0 4px;font-size:8px;letter-spacing:.04em}
          .fabric-fx .fabric-pill i{display:none}
          .fabric-fx .fabric-status span:last-child{display:none}
        }
      `}</style>
    </section>
  );
}